/**
 * Runtime detection and native capabilities.
 *
 * Every native feature is behind a capability check that degrades rather than throws,
 * because the same bundle runs in a browser where none of them exist. Tauri's mobile
 * plugin coverage also lags desktop, so a plugin that works on Windows may be absent on
 * Android -- the check has to be per-feature, not per-platform.
 */

export type Platform = "browser" | "windows" | "macos" | "linux" | "android" | "ios";

export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

let cachedPlatform: Platform | null = null;

export async function detectPlatform(): Promise<Platform> {
  if (cachedPlatform) return cachedPlatform;
  if (!isTauri()) {
    cachedPlatform = "browser";
    return cachedPlatform;
  }
  try {
    const { platform } = await import("@tauri-apps/plugin-os");
    // Synchronous since plugin-os v2: it reads a value the shell injected at startup.
    const name = platform();
    cachedPlatform = (
      {
        windows: "windows",
        macos: "macos",
        linux: "linux",
        android: "android",
        ios: "ios",
      } as Record<string, Platform>
    )[name] ?? "browser";
  } catch {
    cachedPlatform = "browser";
  }
  return cachedPlatform;
}

export function isMobileViewport(): boolean {
  // Viewport rather than platform: a narrow desktop window should get the phone layout,
  // and a tablet should get the workbench.
  return typeof window !== "undefined" && window.innerWidth < 768;
}

/**
 * Send a notification, for the after-hours briefing.
 *
 * Falls back to the Web Notification API in a browser, and to nothing at all where
 * permission is denied. A missing notification is not an error worth surfacing.
 */
export async function notify(title: string, body: string): Promise<void> {
  if (isTauri()) {
    try {
      const { isPermissionGranted, requestPermission, sendNotification } = await import(
        "@tauri-apps/plugin-notification"
      );
      const granted = (await isPermissionGranted()) || (await requestPermission()) === "granted";
      if (granted) sendNotification({ title, body });
      return;
    } catch {
      /* fall through to the web path */
    }
  }

  if (typeof Notification === "undefined") return;
  try {
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    } else if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      if (permission === "granted") new Notification(title, { body });
    }
  } catch {
    /* notifications unavailable */
  }
}
